// Modules:
import React from 'react';
import { useTheme } from '@mui/material/styles';

// Style:
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Utils:
import { customLegendStyleNames } from 'utilities/utils';

const PointMarker = ({
    breakInfo,
    layerOn,
    theme,
    labelText,
    missingSuppressed,
    setMissingSuppressedModalOpen,
    opacity = 1,
    customStyleTitle,
}) => {
    const [r, g, b] = breakInfo.color;
    const label = breakInfo.label || labelText;

    return (
        <div
            className={`legend-item ${
                missingSuppressed ? 'missing-suppressed' : ''
            } ${breakInfo.style === 'esriSMSCircle' ? 'center-icon' : ''}`}
            key={breakInfo.label}
            onClick={
                missingSuppressed
                    ? () => setMissingSuppressedModalOpen(true)
                    : null
            }
            onKeyDown={
                missingSuppressed
                    ? (e) => {
                          if (e.key === 'Enter') {
                              e.preventDefault();
                              setMissingSuppressedModalOpen(true);
                          }
                      }
                    : null
            }
            role={missingSuppressed ? 'button' : null}
            tabIndex={missingSuppressed ? 0 : null}
        >
            <div
                className={breakInfo.style}
                style={
                    // This references the object within the 'utils.js' file.  This returns an object containing the custom
                    // style logic for this layer based on the style configuration from the FeatureLayer.
                    customStyleTitle
                        ? customLegendStyleNames[customStyleTitle].styleMethod(
                              breakInfo,
                              layerOn
                          )
                        : {
                              // Fallback default point style
                              height: `${breakInfo.size * 1}px`,
                              width: `${breakInfo.size * 1}px`,
                              borderColor: layerOn
                                  ? `rgb(${r * 0.8},${g * 0.8},${b * 0.8})` // Lower the rgb values to get a darker border
                                  : theme.palette.grey[300],
                              backgroundColor: layerOn
                                  ? `rgba(${r},${g},${b},${opacity})`
                                  : 'transparent',
                          }
                }
            ></div>
            <div>
                {label}
                {missingSuppressed ? (
                    <Typography>
                        <InfoOutlinedIcon />
                    </Typography>
                ) : null}
            </div>
        </div>
    );
};

const ChoroplethSymbol = ({
    breakInfo,
    layerOn,
    theme,
    addMargin,
    labelText,
    missingSuppressed,
    setMissingSuppressedModalOpen,
    opacity = 1,
}) => {
    const [r, g, b] = breakInfo.color;
    const label = breakInfo.label || labelText;
    const determineBgColor = (active, content) => {
        if (content !== 'Missing' && active) {
            return opacity
                ? `rgba(${r},${g},${b},${opacity})`
                : `rgb(${r},${g},${b})`;
        }
        if (content !== 'Missing' && !active) {
            return theme.palette.grey[300];
        }

        return 'rgb(255, 255, 255)';
    };

    return (
        <div
            className={`legend-item ${!layerOn ? 'layer-off' : ''} ${
                missingSuppressed ? 'missing-suppressed' : ''
            }`}
            key={breakInfo.label}
            style={{ marginLeft: addMargin ? '25px' : null }}
            onClick={
                missingSuppressed
                    ? () => setMissingSuppressedModalOpen(true)
                    : null
            }
            onKeyDown={
                missingSuppressed
                    ? (e) => {
                          if (e.key === 'Enter') {
                              e.preventDefault();
                              setMissingSuppressedModalOpen(true);
                          }
                      }
                    : null
            }
            role={missingSuppressed ? 'button' : null}
            tabIndex={missingSuppressed ? 0 : null}
        >
            <div
                className={breakInfo.style}
                style={{
                    backgroundColor: determineBgColor(layerOn, breakInfo.label),
                    border:
                        // eslint-disable-next-line no-nested-ternary
                        layerOn && breakInfo.label === 'Missing'
                            ? 'black solid 1px'
                            : !layerOn && breakInfo.label === 'Missing'
                            ? `${theme.palette.grey[300]} solid 1px`
                            : null,
                }}
            >
                <div
                    className={`legend-item-label ${
                        label?.length < 15 ? '' : 'label-breaks'
                    }`}
                >
                    {label}
                    {missingSuppressed ? (
                        <Typography>
                            <InfoOutlinedIcon />
                        </Typography>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

// A color block swatchabove its label, for layers whose renderer assigns one color per value.
// The Radon physiographic provinces are currently the only layer using this.
const SwatchSymbol = ({ breakInfo, labelText, layerOn, theme }) => {
    const [r, g, b] = breakInfo.color;

    return (
        <div className="legend-swatch-item">
            <div
                className="legend-swatch"
                style={{
                    backgroundColor: layerOn
                        ? `rgb(${r},${g},${b})`
                        : theme.palette.grey[300],
                }}
            />
            <div>{labelText}</div>
        </div>
    );
};

const LegendItems = ({ layer, layerOn, setMissingSuppressedModalOpen }) => {
    // Hooks
    const theme = useTheme();

    // If unable to load legend data for some reason, immediately return error message:
    if (layer.renderInfo.error) {
        return (
            <h6 className="legend-error-message">Unable to load legend data</h6>
        );
    }
    // Use image data stored in config file, if custom point symbol
    if (layer.customPointSymbol) {
        const src = `data:${layer.drawingInfo.renderer.symbol.contentType};base64,${layer.drawingInfo.renderer.symbol.imageData}`;
        return (
            <img
                alt={layer.symbolDescription}
                src={src}
                className={layerOn ? '' : 'grayscale'}
            />
        );
    }

    // Use image data coming from service to inherit symbol
    if (layer.renderInfo.type === 'simple') {
        if (layer.renderInfo.symbol.contentType === 'image/png') {
            const src = `data:${layer.renderInfo.symbol.contentType};base64,${layer.renderInfo.symbol.imageData}`;
            return (
                <img
                    alt={layer.symbolDescription}
                    src={src}
                    className={layerOn ? '' : 'grayscale'}
                />
            );
        }
        if (layer.renderInfo.symbol.style === 'esriSMSCircle') {
            return (
                <PointMarker
                    breakInfo={layer.renderInfo.symbol}
                    layerOn={layerOn}
                    theme={theme}
                />
            );
        }
    }

    // Determine symbol style and layer type:
    const itemStyle = layer.renderInfo.classBreakInfos
        ? layer.renderInfo.classBreakInfos[0].symbol.style
        : null;
    const customBreaks = !layer.renderInfo.visualVariables;

    if (customBreaks && itemStyle) {
        const nonMissingSuppressedSymbols = layer.renderInfo.classBreakInfos
            .filter(
                (breakPoint) =>
                    breakPoint.label !== 'Missing' &&
                    breakPoint.label !== 'Suppressed'
            )
            .map((breakInfo) => {
                const data = {
                    style: itemStyle,
                    color: breakInfo.symbol.color,
                    size: breakInfo.symbol.size,
                    label: breakInfo.label,
                };

                if (itemStyle === 'esriSMSCircle') {
                    return (
                        <PointMarker
                            key={data.label}
                            breakInfo={data}
                            layerOn={layerOn}
                            theme={theme}
                        />
                    );
                }
                if (itemStyle === 'esriSFSSolid') {
                    return (
                        <ChoroplethSymbol
                            key={data.label}
                            breakInfo={data}
                            layerOn={layerOn}
                            theme={theme}
                        />
                    );
                }
                // TODO: fallback symbology?
                return null;
            });

        const missingSuppressedSymbols = layer.renderInfo.classBreakInfos
            .filter(
                (breakPoint) =>
                    breakPoint.label === 'Missing' ||
                    breakPoint.label === 'Suppressed'
            )
            .map((breakInfo, index) => {
                const data = {
                    style: itemStyle,
                    color: breakInfo.symbol.color,
                    size: breakInfo.symbol.size,
                    label: breakInfo.label,
                };

                if (itemStyle === 'esriSMSCircle') {
                    return (
                        <PointMarker
                            key={data.label}
                            breakInfo={data}
                            layerOn={layerOn}
                            theme={theme}
                            missingSuppressed
                            setMissingSuppressedModalOpen={
                                setMissingSuppressedModalOpen
                            }
                        />
                    );
                }
                if (itemStyle === 'esriSFSSolid') {
                    return (
                        <ChoroplethSymbol
                            key={data.label}
                            breakInfo={data}
                            layerOn={layerOn}
                            theme={theme}
                            addMargin={index === 0}
                            missingSuppressed
                            setMissingSuppressedModalOpen={
                                setMissingSuppressedModalOpen
                            }
                        />
                    );
                }
                // TODO: fallback symbology?
                return null;
            });

        return [nonMissingSuppressedSymbols, missingSuppressedSymbols];
    }

    if (layer.renderInfo.type === 'simple') {
        return (
            <ChoroplethSymbol
                breakInfo={layer.renderInfo.symbol}
                layerOn={layerOn}
                theme={theme}
            />
        );
    }

    if (layer.renderInfo.type === 'uniqueValue') {
        if (layer.rendererInfoType === 'uniquePoint') {
            return layer.renderInfo.uniqueValueInfos.map((info) => (
                <PointMarker
                    key={info.label}
                    breakInfo={info.symbol}
                    layerOn={layerOn}
                    theme={theme}
                    labelText={info.label}
                    opacity={0.5}
                    customStyleTitle={layer.customStyleTitle || null} // The presence of a 'customStyleTitle' indicates if additional custom styling is needed.
                />
            ));
        }
        if (layer.rendererInfoType === 'uniqueChoropleth') {
            return layer.renderInfo.uniqueValueInfos.map((info) => (
                <ChoroplethSymbol
                    key={info.label}
                    breakInfo={info.symbol}
                    layerOn={layerOn}
                    theme={theme}
                    addMargin
                    labelText={info.label}
                    opacity={0.5}
                />
            ));
        }

        // Any other uniqueValue layer, including one with no rendererInfoType, shows a color
        // block per value. Only the Radon physiographic provinces reach this today. Wrapped in
        // one container so the blocks wrap as a group.
        return (
            <div className="legend-swatch-list">
                {layer.renderInfo.uniqueValueInfos.map((info) => (
                    <SwatchSymbol
                        key={info.label}
                        breakInfo={info.symbol}
                        labelText={info.label}
                        layerOn={layerOn}
                        theme={theme}
                    />
                ))}
            </div>
        );
    }

    return layer.renderInfo.visualVariables
        ?.find((visualVariable) => visualVariable.stops)
        .stops.map((breakInfo) => {
            const data = {
                style: itemStyle,
                color: breakInfo.color,
                size: breakInfo.value,
                value: breakInfo.value,
                label: breakInfo.label,
            };
            if (itemStyle === 'esriSMSCircle') {
                return (
                    <PointMarker
                        key={data.value}
                        breakInfo={data}
                        layerOn={layerOn}
                        theme={theme}
                    />
                );
            }
            if (itemStyle === 'esriSFSSolid') {
                return (
                    <ChoroplethSymbol
                        key={data.value}
                        breakInfo={data}
                        layerOn={layerOn}
                        theme={theme}
                    />
                );
            }

            // TODO: fallback symbology?
            return null;
        });
};

export default LegendItems;
